import React from "react";
import { Link } from "react-router-dom";
import { Info, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBilling } from "@/contexts/BillingContext";

/**
 * "Last call" banner — surfaces when the user is 1-2 summaries away from
 * the paywall. Soft blue, information-forward (not alarmist). Hidden for
 * Pro users and anyone who has already hit the paywall.
 *
 * No red styling anywhere: SUX1's house palette is blue + white only,
 * including for anything that would classically be an "alert".
 */
export const LastCallBanner = () => {
  const { t } = useTranslation();
  const { billing } = useBilling();

  if (!billing || billing.is_pro) return null;

  const used = billing.free_summaries_used ?? 0;
  const total = billing.free_summary_limit ?? 0;
  const remaining = Math.max(0, total - used);
  const isAnon = !!billing.is_anonymous;

  // Only show in the last 1-2 window (per auth state).
  if (total === 0 || remaining === 0) return null;
  if (isAnon || remaining !== 1) return null;

  return (
    <div
      data-testid="last-call-banner"
      className="relative border border-[#002FA7] bg-[#002FA7]/5 text-foreground px-4 py-3 mb-6 rounded-sm"
    >
      {/* One compact row on all viewports — avoids per-word wrapping and
          CTA overlap seen when the row was multi-column on narrow screens. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <Info className="w-4 h-4 text-[#002FA7] shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-[#002FA7] mb-0.5 whitespace-normal">
              {t("last_call.marker", { count: remaining })}
            </div>
            <div className="text-[13px] leading-snug text-foreground/90 whitespace-normal">
              {t("last_call.body")}
            </div>
          </div>
        </div>
        <Link
          to={isAnon ? "/signup" : "/billing/upgrade"}
          data-testid="last-call-cta"
          className="font-mono uppercase tracking-[0.14em] text-[11px] bg-[#002FA7] text-white px-4 py-2.5 hover:bg-[#00227A] transition-colors inline-flex items-center justify-center gap-2 shrink-0 whitespace-nowrap w-full sm:w-auto"
        >
          <Sparkles className="w-3 h-3" />
          {isAnon
            ? t("last_call.cta_anon", "Créer un compte")
            : t("last_call.cta")}
        </Link>
      </div>
    </div>
  );
};

export default LastCallBanner;
